/**
 * HaloThemeContext — runtime control of halo design tokens.
 *
 * Three axes:
 *   - theme:   "light" | "dark" | "system"   (system follows prefers-color-scheme)
 *   - palette: "forest" | "cobalt" | "rust" | "ink"
 *   - density: "compact" | "regular" | "comfy"
 *
 * Persistence: `localStorage.haloPrefs` (JSON). Anti-FOUC bootstrap is in
 * public/index.html (sets data-halo-* on <html> synchronously before React mounts).
 *
 * The provider writes data-halo-theme, data-halo-palette, data-halo-density
 * onto document.documentElement; halo-tokens.css consumes those attributes.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type HaloTheme = 'light' | 'dark' | 'system';
export type HaloPalette = 'forest' | 'cobalt' | 'rust' | 'ink';
export type HaloDensity = 'compact' | 'regular' | 'comfy';

export interface HaloPrefs {
  theme: HaloTheme;
  palette: HaloPalette;
  density: HaloDensity;
}

export interface HaloThemeContextValue extends HaloPrefs {
  /** Resolved theme — "system" collapses to "light" or "dark". */
  resolvedTheme: 'light' | 'dark';
  setTheme: (t: HaloTheme) => void;
  setPalette: (p: HaloPalette) => void;
  setDensity: (d: HaloDensity) => void;
  setPrefs: (next: Partial<HaloPrefs>) => void;
}

const STORAGE_KEY = 'haloPrefs';
const DEFAULTS: HaloPrefs = {
  theme: 'system',
  palette: 'forest',
  density: 'regular',
};

function readStoredPrefs(): HaloPrefs {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<HaloPrefs>;
    return {
      theme: (parsed.theme as HaloTheme) || DEFAULTS.theme,
      palette: (parsed.palette as HaloPalette) || DEFAULTS.palette,
      density: (parsed.density as HaloDensity) || DEFAULTS.density,
    };
  } catch (_err) {
    return DEFAULTS;
  }
}

function writeStoredPrefs(prefs: HaloPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (_err) {
    /* localStorage may be unavailable (private mode, quota) — ignore. */
  }
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyToRoot(prefs: HaloPrefs, resolved: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-halo-theme', resolved);
  root.setAttribute('data-halo-palette', prefs.palette);
  root.setAttribute('data-halo-density', prefs.density);
  // Mirror Tailwind's `dark` class so `dark:` utility variants trigger off
  // the same source of truth. Keep in sync with the anti-FOUC bootstrap
  // script in public/index.html.
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

const HaloThemeContext = createContext<HaloThemeContextValue | null>(null);

export interface HaloThemeProviderProps {
  children: React.ReactNode;
}

export const HaloThemeProvider: React.FC<HaloThemeProviderProps> = ({ children }) => {
  const [prefs, setPrefsState] = useState<HaloPrefs>(() => readStoredPrefs());
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => getSystemTheme());

  const resolvedTheme: 'light' | 'dark' =
    prefs.theme === 'system' ? systemTheme : prefs.theme;

  // Apply to root whenever resolved values change.
  useEffect(() => {
    applyToRoot(prefs, resolvedTheme);
  }, [prefs, resolvedTheme]);

  // Persist prefs.
  useEffect(() => {
    writeStoredPrefs(prefs);
  }, [prefs]);

  // Subscribe to OS theme changes while in "system" mode.
  useEffect(() => {
    if (prefs.theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    // Modern browsers expose addEventListener; older Safari only addListener.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    // Legacy Safari fallback. `MediaQueryList.addListener` is deprecated but
    // still typed in lib.dom.d.ts — no suppression directive needed.
    mq.addListener(handler);
    return () => {
      mq.removeListener(handler);
    };
  }, [prefs.theme]);

  const setPrefs = useCallback((next: Partial<HaloPrefs>) => {
    setPrefsState((prev) => ({ ...prev, ...next }));
  }, []);

  const setTheme = useCallback((t: HaloTheme) => setPrefs({ theme: t }), [setPrefs]);
  const setPalette = useCallback((p: HaloPalette) => setPrefs({ palette: p }), [setPrefs]);
  const setDensity = useCallback((d: HaloDensity) => setPrefs({ density: d }), [setPrefs]);

  const value = useMemo<HaloThemeContextValue>(
    () => ({
      theme: prefs.theme,
      palette: prefs.palette,
      density: prefs.density,
      resolvedTheme,
      setTheme,
      setPalette,
      setDensity,
      setPrefs,
    }),
    [prefs, resolvedTheme, setTheme, setPalette, setDensity, setPrefs],
  );

  return <HaloThemeContext.Provider value={value}>{children}</HaloThemeContext.Provider>;
};

HaloThemeProvider.displayName = 'HaloThemeProvider';

export function useHaloTheme(): HaloThemeContextValue {
  const ctx = useContext(HaloThemeContext);
  if (!ctx) {
    throw new Error('useHaloTheme must be used inside <HaloThemeProvider>');
  }
  return ctx;
}

export default HaloThemeProvider;
